import React, { useState, useEffect, useCallback } from 'react';

import { Select, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import SelectNotFoundContent from '../ui/SelectNotFoundContent';
import CreateTemplateModal from './CreateTemplateModal';

// Sentinel id for the built-in "全部權限" virtual option.
// 不對應任何 PermissionTemplate 紀錄；選中時直接送出 ["*:*"]。
export const SUPER_ADMIN_TEMPLATE_VALUE = '__super_admin__';

/**
 * 權限範本選擇器（邀請表單用）
 *
 * @param {Object} templateService - injected service ({ fetchList, upsert, delete })
 * @param {String} role - 'operator' | 'admin' (passed to inner CreateTemplateModal)
 * @param {String} value - 選中的模板 ID
 * @param {Function} onChange - (templateId, permissions[]) => void
 */
const TemplateSelect = ({ templateService, role, value, onChange }) => {
  const { t } = useTranslation();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await templateService.fetchList();
      setTemplates(data.data || []);
      return data.data || [];
    } catch (error) {
      console.error('Failed to load permission templates:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [templateService]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleChange = (templateId) => {
    if (!templateId) {
      onChange(null, []);
      return;
    }
    if (templateId === SUPER_ADMIN_TEMPLATE_VALUE) {
      onChange(SUPER_ADMIN_TEMPLATE_VALUE, ['*:*']);
      return;
    }
    const template = templates.find(tpl => tpl.id === templateId);
    if (template) {
      onChange(templateId, template.permissions || []);
    }
  };

  const handleTemplateCreated = async (template) => {
    setCreateModalOpen(false);
    const refreshed = await fetchTemplates();
    if (template?.id) {
      const created = refreshed.find(tpl => tpl.id === template.id) || template;
      onChange(created.id, created.permissions || []);
    }
  };

  // 生成權限摘要文字（Tooltip 用）
  const buildPermissionSummary = (permissions) => {
    if (!permissions || permissions.length === 0) {
      return t('common.permissions_editor.no_permissions_selected');
    }

    if (permissions.includes('*:*')) {
      return t('common.permissions_editor.super_admin_all');
    }

    // 按 resource 分組
    const grouped = {};
    permissions.forEach(p => {
      const [resource, action] = p.split(':');
      if (resource === '*') return; // 跳過全域萬用字元
      if (!grouped[resource]) grouped[resource] = [];
      if (action === '*') {
        grouped[resource] = ['*'];
      } else if (!grouped[resource].includes('*')) {
        grouped[resource].push(action);
      }
    });

    const entries = Object.entries(grouped);
    const maxDisplay = 8;
    const displayEntries = entries.slice(0, maxDisplay);
    const remaining = entries.length - maxDisplay;

    const lines = displayEntries.map(([resource, actions]) => {
      const resourceName = t(`common.resources.${resource}`, resource);
      const actionNames = actions[0] === '*'
        ? t('common.permission_actions.*', '全部')
        : actions.map(a => t(`common.permission_actions.${a}`, a)).join('、');
      return `${resourceName}：${actionNames}`;
    });

    if (remaining > 0) {
      lines.push(t('common.template_select.and_more', { count: remaining }));
    }

    return lines.join('\n');
  };

  return (
    <>
      <Select
        value={value}
        onChange={handleChange}
        allowClear
        loading={loading}
        placeholder={t('common.permissions_editor.select_template_placeholder')}
        optionLabelProp="label"
        notFoundContent={
          <SelectNotFoundContent
            hint={t('common.permissions_editor.no_templates')}
            settingsLabel={t('common.template_select.create_template')}
            onAction={() => setCreateModalOpen(true)}
            onReload={fetchTemplates}
            reloadLoading={loading}
          />
        }
      >
        <Select.Option
          key={SUPER_ADMIN_TEMPLATE_VALUE}
          value={SUPER_ADMIN_TEMPLATE_VALUE}
          label={t('common.permissions_editor.super_admin_all')}
        >
          <Tooltip
            title={t('common.permissions_editor.super_admin_all')}
            placement="right"
            mouseEnterDelay={0.3}
          >
            <div>{t('common.permissions_editor.super_admin_all')}</div>
          </Tooltip>
        </Select.Option>
        {templates.map(tpl => (
          <Select.Option key={tpl.id} value={tpl.id} label={tpl.name}>
            <Tooltip
              title={<pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '12px' }}>{buildPermissionSummary(tpl.permissions)}</pre>}
              placement="right"
              mouseEnterDelay={0.3}
            >
              <div>{tpl.name}</div>
            </Tooltip>
          </Select.Option>
        ))}
      </Select>

      <CreateTemplateModal
        open={createModalOpen}
        templateService={templateService}
        role={role}
        onClose={() => setCreateModalOpen(false)}
        onCreated={handleTemplateCreated}
      />
    </>
  );
};

export default TemplateSelect;
